import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import { ArrowUpRight, Target, Eye, Mountain, MapPin, Briefcase } from "lucide-react";

const founderJsonLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Leo Wagenhofer",
  jobTitle: "Gründer und Geschäftsführer",
  worksFor: {
    "@type": "Organization",
    name: "Wagenhofer Capital",
    url: "https://wagenhofer.capital",
  },
  url: "https://wagenhofer.capital/ueber-uns#gruender",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Neubiberg",
    addressRegion: "Bayern",
    addressCountry: "DE",
  },
  description:
    "Leo Wagenhofer ist Gründer und Geschäftsführer von Wagenhofer Capital. Mit unternehmerischem Fokus auf Beteiligungen, operative Entwicklung, Gastronomie und Events bündelt Wagenhofer Capital verschiedene Projekte und Gesellschaften unter einer langfristig ausgerichteten Unternehmensstruktur.",
};

const pillars = [
  {
    icon: Target,
    label: "Mission",
    title: "Werte langfristig schaffen.",
    text: "Wir bauen, führen und entwickeln Unternehmen, Beteiligungen und Projekte mit unternehmerischer Verantwortung. Substanz, Struktur und Stabilität bestimmen unser Handeln.",
  },
  {
    icon: Eye,
    label: "Vision",
    title: "Vertrauenswürdige Heimat für unternehmerische Substanz.",
    text: "Aus dem Raum München heraus eine der respektiertesten unternehmerischen Beteiligungsgesellschaften des deutschsprachigen Raums – diskret, verlässlich, anspruchsvoll.",
  },
  {
    icon: Mountain,
    label: "Haltung",
    title: "Geduld als strategischer Vorteil.",
    text: "Wir denken in Jahrzehnten. Geschwindigkeit ist Mittel, nicht Ziel. Reife Entscheidungen entstehen aus Erfahrung, Analyse und unternehmerischem Mut.",
  },
];

export default function UeberUns() {
  useRevealOnScroll();

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <SEO
        title="Über uns | Wagenhofer Capital – Leo Wagenhofer aus München"
        description="Wagenhofer Capital ist die Beteiligungs- und Verwaltungsgesellschaft von Leo Wagenhofer aus dem Raum München. Mission, Vision und Struktur einer Holding mit langfristiger Perspektive."
        path="/ueber-uns"
        jsonLd={founderJsonLd}
      />
      <Navigation />
      <Breadcrumbs items={[{ label: "Über uns", to: "/ueber-uns" }]} />

      <PageHeader
        testId="page-about"
        overline="Über uns"
        title={<>Unternehmerisch.<br/>Regional.<br/><span className="text-zinc-400">Verlässlich.</span></>}
        subtitle="Wagenhofer Capital steht für den Aufbau und die strukturierte Führung unternehmerischer Aktivitäten – mit Fokus auf Beteiligungen, operative Projekte und partnerschaftliche Entwicklung im Raum München."
      />

      {/* PROFIL */}
      <section data-testid="section-about-content" className="section-y border-b border-white/5">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          <div className="md:col-span-5 reveal">
            <div className="overline mb-6">Profil</div>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-medium leading-[1.1]">Substanz statt<br/><span className="text-zinc-400">Symbolik.</span></h2>
          </div>
          <div className="md:col-span-7 md:pl-8 reveal delay-1 space-y-6 text-zinc-300 text-base md:text-lg leading-relaxed font-light">
            <p>Wagenhofer Capital wurde von <span className="text-white">Leo Wagenhofer</span> gegründet und steht für den Aufbau und die strukturierte Führung unternehmerischer Aktivitäten. Der Fokus liegt auf Beteiligungen, operativen Projekten und partnerschaftlicher Entwicklung in den Bereichen Beteiligungen, Immobilien, Gastronomie, Events, Dienstleistungen und zukunftsorientierte Geschäftsmodelle.</p>
            <p>Unser Anspruch ist es, Projekte nicht nur finanziell zu betrachten, sondern aktiv zu verstehen, Verantwortung zu übernehmen und langfristige Strukturen zu schaffen – mit der Geduld, die echte Substanz erfordert.</p>
          </div>
        </div>
      </section>

      {/* GRÜNDER */}
      <section
        id="gruender"
        data-testid="section-founder"
        className="section-y border-b border-white/5"
        itemScope
        itemType="https://schema.org/Person"
      >
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5 reveal">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#0a0a0a] border border-white/10">
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at 50% 25%, rgba(212,175,55,0.09) 0%, rgba(0,0,0,0) 60%)",
                }}
              />
              <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
                <span className="overline text-zinc-500">Gründer</span>
                <span className="block w-8 h-px bg-[#D4AF37]" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="font-display text-[6rem] md:text-[8rem] leading-none tracking-tighter text-white/95">
                    LW
                  </div>
                  <span className="block w-12 h-px bg-[#D4AF37] mx-auto mt-6" />
                  <div
                    className="overline text-zinc-400 mt-6"
                    itemProp="name"
                  >
                    Leo Wagenhofer
                  </div>
                  <div
                    className="text-[11px] tracking-[0.24em] uppercase text-zinc-500 mt-2"
                    itemProp="jobTitle"
                  >
                    Gründer & Geschäftsführer
                  </div>
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                <span className="overline text-zinc-600">München</span>
                <span className="overline text-zinc-600">2026</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal delay-1">
            <div className="overline mb-6">Über Leo Wagenhofer</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.08] mb-8">
              Leo Wagenhofer.<br/>
              <span className="text-zinc-400">Gründer von Wagenhofer Capital.</span>
            </h2>
            <div
              className="space-y-6 text-zinc-300 text-base md:text-lg leading-relaxed font-light"
              itemProp="description"
            >
              <p>
                <span className="text-white">Leo Wagenhofer</span> ist Gründer und
                Geschäftsführer von Wagenhofer Capital. Mit unternehmerischem
                Fokus auf Beteiligungen, operative Entwicklung, Gastronomie und
                Events bündelt Wagenhofer Capital verschiedene Projekte und
                Gesellschaften unter einer langfristig ausgerichteten
                Unternehmensstruktur.
              </p>
              <p>
                Sein Ansatz verbindet unternehmerische Praxis mit einer klaren
                strategischen Perspektive. Entscheidungen entstehen aus
                Erfahrung, Analyse und der Bereitschaft, Verantwortung zu
                übernehmen – im eigenen Haus wie in den Beteiligungen.
              </p>
            </div>

            <div className="mt-12 pt-10 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="flex items-center gap-2 overline mb-3 text-[#D4AF37]/80">
                  <Briefcase className="w-3 h-3" strokeWidth={1.5} />
                  Rolle
                </div>
                <div className="text-zinc-200 text-sm leading-relaxed">
                  Gründer & Geschäftsführer
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 overline mb-3 text-[#D4AF37]/80">
                  <MapPin className="w-3 h-3" strokeWidth={1.5} />
                  Standort
                </div>
                <div
                  className="text-zinc-200 text-sm leading-relaxed"
                  itemProp="address"
                  itemScope
                  itemType="https://schema.org/PostalAddress"
                >
                  <span itemProp="addressLocality">Neubiberg</span> ·{" "}
                  <span itemProp="addressRegion">Bayern</span>
                </div>
              </div>
              <div>
                <div className="overline mb-3 text-[#D4AF37]/80">Fokus</div>
                <div className="text-zinc-200 text-sm leading-relaxed">
                  Beteiligungen · Entwicklung · Gastronomie · Events
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="overline mb-4">
                Kontakt zu Leo Wagenhofer über Wagenhofer Capital
              </div>
              <Link
                to="/kontakt"
                data-testid="founder-cta-contact"
                className="group inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold"
              >
                Anfrage senden
                <ArrowUpRight
                  className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  strokeWidth={1.5}
                />
              </Link>
            </div>

            <meta itemProp="url" content="https://wagenhofer.capital/ueber-uns#gruender" />
          </div>
        </div>
      </section>

      {/* MISSION VISION HALTUNG */}
      <section data-testid="section-pillars" className="section-y border-b border-white/5 bg-[#070707]">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
            {pillars.map((p, idx) => {
              const Icon = p.icon;
              return (
                <article key={p.label} data-testid={`pillar-${p.label.toLowerCase()}`} className="group relative p-10 md:p-12 border-r border-b border-white/10 hover:bg-black transition-colors duration-500 reveal" style={{ transitionDelay: `${idx * 80}ms` }}>
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-8" strokeWidth={1.25} />
                  <div className="overline mb-3">{p.label}</div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium mb-4 leading-snug">{p.title}</h3>
                  <p className="text-zinc-400 leading-relaxed font-light text-[15px]">{p.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* STRUKTUR */}
      <section id="struktur" data-testid="section-struktur" className="section-y border-b border-white/5">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="md:col-span-5 reveal">
            <div className="overline mb-6">Struktur & Beteiligungen</div>
            <h2 lang="de" className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-medium leading-[1.1] hyphens-auto break-words">Eine klare<br/><span className="text-zinc-400">Dachstruktur.</span></h2>
          </div>
          <div className="md:col-span-7 md:pl-8 reveal delay-1">
            <div className="space-y-6 text-zinc-300 text-base md:text-lg leading-relaxed font-light">
              <p>Wagenhofer Capital dient als Dach für bestehende und zukünftige unternehmerische Aktivitäten. Die Gesellschaft bündelt Projekte, Beteiligungen und operative Einheiten mit dem Ziel, klare Verantwortlichkeiten, professionelle Führung und nachhaltige Entwicklung zu schaffen.</p>
            </div>
            <div className="mt-12 pt-10 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="overline mb-3 text-[#D4AF37]/80">Rolle</div>
                <div className="text-zinc-200 text-sm leading-relaxed">Beteiligungs- und Verwaltungsgesellschaft</div>
              </div>
              <div>
                <div className="overline mb-3 text-[#D4AF37]/80">Fokus</div>
                <div className="text-zinc-200 text-sm leading-relaxed">Operative Projekte & langfristige Strukturen</div>
              </div>
              <div>
                <div className="overline mb-3 text-[#D4AF37]/80">Region</div>
                <div className="text-zinc-200 text-sm leading-relaxed">Raum München · Bayern</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="reveal">
            <div className="overline mb-3">Nächster Schritt</div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight font-medium">Sprechen wir konkret über Ihr Anliegen.</h2>
          </div>
          <Link to="/kontakt" data-testid="about-cta-contact" className="reveal delay-1 group inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold">
            Kontakt aufnehmen
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
